import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { QUIZ_QUESTIONS } from "../../lib/quiz";
import { submitQuiz } from "../../lib/api";
import { ArrowRight, CheckCircle, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

export const QuizModal = ({ open, onOpenChange, onWantDemo }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [contact, setContact] = useState({ name: "", email: "", company: "" });

  const total = QUIZ_QUESTIONS.length;
  const isContactStep = step === total;
  const current = !isContactStep ? QUIZ_QUESTIONS[step] : null;

  const reset = () => {
    setStep(0);
    setAnswers({});
    setResult(null);
    setContact({ name: "", email: "", company: "" });
    setSubmitting(false);
  };

  const handleClose = (val) => {
    if (!val) setTimeout(reset, 250);
    onOpenChange(val);
  };

  const choose = (option) => {
    setAnswers((a) => ({ ...a, [current.id]: option }));
    setTimeout(() => setStep((s) => s + 1), 180);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payloadAnswers = Object.entries(answers).map(([qid, opt]) => ({
        question_id: qid,
        answer: opt.label,
        score: opt.score,
      }));
      const total_score = payloadAnswers.reduce((s, a) => s + a.score, 0);
      const r = await submitQuiz({
        name: contact.name,
        email: contact.email || null,
        company: contact.company,
        answers: payloadAnswers,
        total_score,
      });
      setResult(r);
      toast.success("Результаты отправлены", {
        description: "Мы свяжемся с вами в течение 24 часов.",
      });
    } catch (e) {
      toast.error("Не удалось отправить", { description: "Попробуйте ещё раз" });
    } finally {
      setSubmitting(false);
    }
  };

  const progress = isContactStep ? 100 : Math.round((step / total) * 100);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="quiz-modal"
        className="bg-ink-surface border border-ink-text/10 text-ink-text max-w-2xl p-0 overflow-hidden"
      >
        <div className="relative p-7 sm:p-9">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold tracking-tight text-2xl">
              Оценка потенциала InBoost
            </DialogTitle>
            <DialogDescription className="text-muted mono text-xs uppercase tracking-widest mt-1">
              5 вопросов · ~60 секунд
            </DialogDescription>
          </DialogHeader>

          {/* Progress */}
          <div className="mt-6 h-1 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-brand to-brand-cyan transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mt-2 mono text-[10px] uppercase tracking-widest text-muted">
            {result ? "Готово" : isContactStep ? "Шаг 6 · Контакты" : `Шаг ${step + 1} / ${total}`}
          </div>

          {/* Body */}
          <div className="mt-7 min-h-[280px]">
            {result ? (
              <div data-testid="quiz-result" className="text-center py-6">
                <div className="inline-flex w-14 h-14 rounded-full bg-success/10 border border-success/40 items-center justify-center mb-5">
                  <CheckCircle size={26} weight="duotone" className="text-success" />
                </div>
                <div className="mono text-[10px] uppercase tracking-widest text-brand-cyan">
                  Уровень готовности
                </div>
                <div className="mt-2 font-heading text-3xl font-bold text-brand-cyan text-glow-cyan">
                  {result.level}
                </div>
                <div className="mt-1 mono text-sm text-muted">
                  Балл: <span className="text-ink-text">{result.total_score}</span>
                </div>
                <p className="mt-5 text-sm text-muted max-w-md mx-auto leading-relaxed">
                  {result.recommendation}
                </p>
                <div className="mt-7 flex flex-wrap justify-center gap-3">
                  <button
                    onClick={() => { handleClose(false); onWantDemo?.(); }}
                    className="btn-primary rounded-lg px-5 py-2.5 font-semibold inline-flex items-center gap-2"
                    data-testid="quiz-result-demo-btn"
                  >
                    Запросить демо <ArrowRight size={16} weight="bold" />
                  </button>
                  <button
                    onClick={() => handleClose(false)}
                    className="btn-secondary rounded-lg px-5 py-2.5 font-semibold"
                  >
                    Закрыть
                  </button>
                </div>
              </div>
            ) : current ? (
              <div data-testid="quiz-question">
                <div className="font-heading font-semibold text-xl mb-5">{current.title}</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {current.options.map((opt, i) => {
                    const selected = answers[current.id]?.label === opt.label;
                    return (
                      <button
                        key={i}
                        onClick={() => choose(opt)}
                        data-testid={`quiz-opt-${current.id}-${i}`}
                        className={`group text-left rounded-xl border p-4 transition-all duration-200 ${
                          selected
                            ? "border-brand-cyan bg-brand/10"
                            : "border-ink-text/10 bg-ink-elevated hover:border-brand-cyan/40 hover:bg-brand/5"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm">{opt.label}</span>
                          <span
                            className={`w-2.5 h-2.5 rounded-full transition ${
                              selected ? "bg-brand-cyan shadow-[0_0_12px_#0099C9]" : "bg-ink-text/15"
                            }`}
                          />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              // Contact form before final submit
              <div>
                <div className="font-heading font-semibold text-xl mb-2">Куда выслать результаты?</div>
                <p className="text-sm text-muted mb-5">
                  Мы пришлём краткий отчёт и предложение по пилоту.
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input
                    label="Имя"
                    value={contact.name}
                    onChange={(v) => setContact((c) => ({ ...c, name: v }))}
                    testid="quiz-name"
                  />
                  <Input
                    label="Компания"
                    value={contact.company}
                    onChange={(v) => setContact((c) => ({ ...c, company: v }))}
                    testid="quiz-company"
                  />
                  <div className="sm:col-span-2">
                    <Input
                      label="Email"
                      type="email"
                      value={contact.email}
                      onChange={(v) => setContact((c) => ({ ...c, email: v }))}
                      testid="quiz-email"
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-between items-center">
                  <button
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    className="text-sm text-muted hover:text-ink-text"
                  >
                    ← Назад
                  </button>
                  <button
                    onClick={submit}
                    disabled={submitting}
                    data-testid="quiz-submit-btn"
                    className="btn-primary rounded-lg px-6 py-2.5 font-semibold inline-flex items-center gap-2 disabled:opacity-60"
                  >
                    <Sparkle size={16} weight="fill" />
                    {submitting ? "Отправляем…" : "Получить результат"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Input = ({ label, value, onChange, type = "text", testid }) => (
  <label className="block">
    <div className="mono text-[10px] uppercase tracking-widest text-muted mb-1.5">{label}</div>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full rounded-lg bg-ink border border-ink-text/10 px-3.5 py-2.5 text-sm text-ink-text outline-none focus:border-brand-cyan transition placeholder:text-muted-deep"
    />
  </label>
);

export default QuizModal;
